import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ChevronRight from '../../icons/ChevronRight';
import './Blog.css';
import { useTranslation } from 'react-i18next';

function Blog() {
    const { categoryName, slug } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const formattedCategoryName = categoryName?.replace(/-/g, ' ').toLowerCase();

    useEffect(() => {
        const fetchArticles = async () => {
            setLoading(true);
            try {
                const res = await fetch('https://api.prostay.com/WebsiteKnowledgebase/get-blog-articles');
                if (!res.ok) throw new Error('Failed to fetch');
                const data = await res.json();

                setArticles(data);

                const uniqueCategoryNames = [...new Set(data.map(a => a.categoryName))];
                const uniqueCategoryMeta = uniqueCategoryNames.map(name => {
                    const match = data.find(a => a.categoryName === name);
                    return {
                        name,
                        metaTitle: match?.metaTitle || name,
                        metaDescription: match?.metaDescription || ''
                    };
                });

                setCategories(uniqueCategoryMeta);
            } catch (err) {
                console.error('Error loading blog articles:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchArticles();
    }, []);

    const capitalizeWords = (str) => str.replace(/\b\w/g, (char) => char.toUpperCase());

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    };

    const getPreview = (htmlContent) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        let plainText = tempDiv.textContent || tempDiv.innerText || '';
        plainText = plainText.replace(/\[Fig_\d+\]/g, '');
        return plainText.length > 150 ? plainText.substring(0, 200).trim() + '...' : plainText.trim();
    };

    const filteredArticles = formattedCategoryName
        ? articles.filter(a => a.categoryName.toLowerCase() === formattedCategoryName)
        : articles;

    const selectedArticle = slug
        ? filteredArticles.find(a => generateSlug(a.title) === slug)
        : null;

    const selectedCategory = formattedCategoryName
        ? categories.find(c => c.name.toLowerCase() === formattedCategoryName)
        : null;

    const canonicalUrl = `https://www.prostay.com/blog${categoryName ? `/${categoryName}` : ''}${slug ? `/${slug}` : ''}`;

    const pageTitle = selectedArticle
        ? selectedArticle.metaTitle || selectedArticle.title
        : selectedCategory
            ? selectedCategory.metaTitle || selectedCategory.name
            : 'Prostay – Hotel Technology News & Tips Blog';

    const pageDescription = selectedArticle
        ? selectedArticle.metaDescription || getPreview(selectedArticle.content)
        : selectedCategory
            ? selectedCategory.metaDescription || ''
            : 'Read hotel tech trends, expert tips, and news from the Prostay team. Learn how to optimize your operations and guest experience.';

    const handleCategoryClick = (catName) => {
        const slugified = catName.toLowerCase().replace(/\s+/g, '-');
        navigate(`/blog/${slugified}`);
    };

    const handleArticleClick = (article) => {
        const catSlug = article.categoryName.toLowerCase().replace(/\s+/g, '-');
        const articleSlug = generateSlug(article.title);
        navigate(`/blog/${catSlug}/${articleSlug}`);
    };

    return (
        <div className="blog">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>

            <div className="background-image">
                <div className="background-image-start">
                    <div className="d-flex align-items-center gap-2 gap-md-3 breadcrumbs">
                        <a href="/" style={{ textDecoration: 'none' }}><small>{t('Home')}</small></a>
                        <ChevronRight />
                        <a href="/blog" style={{ textDecoration: 'none' }}>
                            <small className={!categoryName ? 'text-primary' : ''}>{t('Blog')}</small>
                        </a>
                        {categoryName && (
                            <>
                                <ChevronRight />
                                <small className={!slug ? 'text-primary' : ''}>
                                    {capitalizeWords(formattedCategoryName)}
                                </small>
                            </>
                        )}
                        {selectedArticle && (
                            <>
                                <ChevronRight />
                                <small className="text-primary">{selectedArticle.title}</small>
                            </>
                        )}
                    </div>
                    <div className="mt-4" style={{ maxWidth: '600px' }}>
                        <h1>{selectedArticle ? selectedArticle.title : selectedCategory?.name || t('Blog Articles')}</h1>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="d-flex align-items-center justify-content-center mt-5">
                    <div className="spinner-container">
                        <div className="custom-spinner"></div>
                    </div>
                </div>
            ) : (
                <div className="d-flex justify-content-center">
                    <div className="content-grid page-content mt-2 mt-md-3">
                        <div className="col">
                            <div className="white-content-card">
                                <h5>{t('Popular Topics')}</h5>
                                <hr />
                                {categories.map(cat => (
                                    <button
                                        key={cat.name}
                                        onClick={() => handleCategoryClick(cat.name)}
                                        className={`subcategory-link ${cat.name.toLowerCase() === formattedCategoryName ? 'active' : ''}`}
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                            <div className="contact-card d-flex flex-column">
                                <div>
                                    <h2 style={{ color: '#FFFFFF', fontSize: '20px' }}>{t('Contact Support')}</h2>
                                    <p style={{ color: '#FFFFFF', textAlign: 'start' }}>{t('24/7 help from our support team')}</p>
                                </div>
                                <a href="/contact-us" style={{ textDecoration: 'none' }}>
                                    <button className="light-blue-filled-btn-small" style={{ backgroundColor: '#22242A', color: '#fff', width: 'fit-content' }}>
                                        <svg width="16" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 4L8.89 9.26C9.22 9.48 9.6 9.6 10 9.6C10.4 9.6 10.78 9.48 11.11 9.26L19 4M3 15H17C17.53 15 18.04 14.79 18.41 14.41C18.79 14.04 19 13.53 19 13V3C19 2.47 18.79 1.96 18.41 1.59C18.04 1.21 17.53 1 17 1H3C2.47 1 1.96 1.21 1.59 1.59C1.21 1.96 1 2.47 1 3V13C1 13.53 1.21 14.04 1.59 14.41C1.96 14.79 2.47 15 3 15Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        {t('Get in Touch')}
                                    </button>
                                </a>
                            </div>
                        </div>

                        <div className="white-content-card">
                            {selectedArticle ? (
                                <div className="article-content" dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
                            ) : (
                                <>
                                    <h4>{selectedCategory?.name || t('All Articles')}</h4>
                                    <div className="d-flex flex-column mt-2 mt-md-4">
                                        {filteredArticles.map(article => (
                                            <div
                                                key={article.id}
                                                className="d-flex flex-column article"
                                                onClick={() => handleArticleClick(article)}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.6, marginBottom: 3 }}>
                                                        <circle cx="10" cy="10" r="8" stroke="#1643E0" strokeWidth="2" />
                                                    </svg>
                                                    <h5 style={{ margin: 0 }}>{article.title}</h5>
                                                </div>
                                                <small className="body-small">{getPreview(article.content)}</small>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Blog;
